import React, { useEffect } from 'react';
import { supabase } from '@/lib/supabaseClient';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { CheckCircle2, XCircle, MoreVertical, Trash2, UserCheck } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';

interface Player {
  id: number;
  name: string;
  whsId: string;
  nominated: boolean;
  matchesPlayed: number;
  matchesWon: number;
  matchesDrawn: number;
  matchesLost: number;
}

interface PlayerTableProps {
  players: Player[];
  teamName: string;
  canEditPlayers: boolean;
  canEditNominations: boolean;
  isOwnTeam: boolean;
  isAdmin: boolean;
  onEditClick: (player: Player) => void;
  onToggleNomination?: (playerId: number) => void;
  onDeletePlayer?: (playerId: number) => void;
  stickyHeader?: boolean;
}

const PlayerTable: React.FC<PlayerTableProps> = ({
  players,
  teamName,
  canEditPlayers,
  canEditNominations,
  isOwnTeam,
  isAdmin,
  onEditClick,
  onToggleNomination,
  onDeletePlayer
}) => {
  const isMobile = useIsMobile();
  const [playersWithStats, setPlayersWithStats] = React.useState(players);
  const [sortColumn, setSortColumn] = React.useState<'played' | 'winPct' | 'whsId' | 'name' | 'nominated'>('played');
  const [sortDirection, setSortDirection] = React.useState<'asc' | 'desc'>('desc');

  const handleNominationToggle = (playerId: number) => {
    if (canEditNominations && typeof onToggleNomination === 'function') {
      onToggleNomination(playerId);
    }
  };

  useEffect(() => {
    const fetchMatchStats = async () => {
      console.log('Players received in PlayerTable:', players);
      const playerIds = players?.map((p) => p.id.toString()).filter(Boolean) || [];
      console.log('Player IDs for stat fetch:', playerIds);
      if (playerIds.length === 0) {
        setPlayersWithStats(players);
        return;
      }

      // Fetch matchups where player appears as home or away player
      const { data: matchups, error } = await supabase
        .from('matchups')
        .select('result, home_player_id, away_player_id')
        .or(`home_player_id.in.(${playerIds.map(id => `"${id}"`).join(',')}),away_player_id.in.(${playerIds.map(id => `"${id}"`).join(',')})`);

      if (error) {
        console.error('Error fetching matchups:', error);
        return;
      }

      if (!matchups || matchups.length === 0) {
        setPlayersWithStats(players);
        return;
      }

      const updatedStats = players.map((player) => {
        const playerMatchups = matchups.filter(
          m => m.home_player_id === player.id || m.away_player_id === player.id
        );
        const stats = {
          matchesPlayed: playerMatchups.length,
          matchesWon: playerMatchups.filter((m) => {
            return (
              (m.result === 'win' && m.home_player_id === player.id) ||
              (m.result === 'loss' && m.away_player_id === player.id)
            );
          }).length,
          matchesDrawn: playerMatchups.filter(
            (m) => m.result === 'half' && (m.home_player_id === player.id || m.away_player_id === player.id)
          ).length,
          matchesLost: playerMatchups.filter((m) => {
            return (
              (m.result === 'loss' && m.home_player_id === player.id) ||
              (m.result === 'win' && m.away_player_id === player.id)
            );
          }).length,
        };

        return {
          ...player,
          ...stats
        };
      });

      let sortedStats = [...updatedStats];

      sortedStats.sort((a, b) => {
        const direction = sortDirection === 'asc' ? 1 : -1;

        if (sortColumn === 'played') {
          return (a.matchesPlayed - b.matchesPlayed) * direction;
        }

        if (sortColumn === 'winPct') {
          const aPct = a.matchesPlayed > 0 ? a.matchesWon / a.matchesPlayed : 0;
          const bPct = b.matchesPlayed > 0 ? b.matchesWon / b.matchesPlayed : 0;
          return (aPct - bPct) * direction;
        }

        if (sortColumn === 'whsId') {
          return (a.whsId || '').localeCompare(b.whsId || '') * direction;
        }

        if (sortColumn === 'nominated') {
          return (a.nominated === b.nominated ? 0 : a.nominated ? -1 : 1) * direction;
        }

        return a.name.localeCompare(b.name) * direction;
      });
      setPlayersWithStats(sortedStats);
    };

    fetchMatchStats();
  }, [players, sortColumn, sortDirection]);

  if (playersWithStats.length === 0) {
    return (
      <div className="text-center py-6 text-muted-foreground">
        No players found for {teamName}. Click "Add Player" to add players to the team.
      </div>
    );
  }

  if (isMobile) {
    return (
      <div className="space-y-4">
        {playersWithStats.map(player => (
          isOwnTeam ? (
            <div key={player.id} className="bg-gray-200 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-sm">{player.name}</div>
                  <div className="text-xs text-muted-foreground">WHS ID: {player.whsId || 'Not set'}</div>
                </div>
              </div>

              <Separator className="my-2" />

              <div className="flex justify-between items-center text-sm">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id={`nominated-${player.id}`}
                    checked={player.nominated}
                    onCheckedChange={() => handleNominationToggle(player.id)}
                    disabled={!canEditNominations}
                    className="border-[#30702F] data-[state=checked]:bg-[#30702F] data-[state=checked]:border-[#30702F]"
                  />
                  <label htmlFor={`nominated-${player.id}`} className="text-sm font-medium leading-none">
                    Nominated
                  </label>
                </div>
                <div className="text-xs text-muted-foreground flex justify-end flex-wrap gap-x-0 gap-y-1 mt-2">
                  <div className="flex flex-col items-center min-w-[28px]">
                    <span className="font-medium">W</span>
                    <span>{player.matchesWon}</span>
                  </div>
                  <div className="flex flex-col items-center min-w-[28px]">
                    <span className="font-medium">H</span>
                    <span>{player.matchesDrawn}</span>
                  </div>
                  <div className="flex flex-col items-center min-w-[28px]">
                    <span className="font-medium">L</span>
                    <span>{player.matchesLost}</span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div key={player.id} className="bg-gray-200 p-3 rounded-lg">
              <div className="flex justify-between items-start">
                <div>
                  <div className="font-medium text-sm">{player.name}</div>
                  <div className="text-xs text-muted-foreground">WHS ID: {player.whsId || 'Not set'}</div>
                  <div className="flex items-center gap-1 mt-1">
                    <Checkbox
                      id={`nominated-${player.id}`}
                      checked={player.nominated}
                      onCheckedChange={() => handleNominationToggle(player.id)}
                      disabled={!canEditNominations}
                      className="border-[#30702F] data-[state=checked]:bg-[#30702F] data-[state=checked]:border-[#30702F] h-4 w-4"
                    />
                    <label htmlFor={`nominated-${player.id}`} className="text-xs text-muted-foreground">
                      Nominated
                    </label>
                  </div>
                </div>
                <div className="flex gap-3 text-sm text-muted-foreground">
                  <div className="flex flex-col items-center min-w-[28px]">
                    <span className="font-medium">W</span>
                    <span>{player.matchesWon}</span>
                  </div>
                  <div className="flex flex-col items-center min-w-[28px]">
                    <span className="font-medium">H</span>
                    <span>{player.matchesDrawn}</span>
                  </div>
                  <div className="flex flex-col items-center min-w-[28px]">
                    <span className="font-medium">L</span>
                    <span>{player.matchesLost}</span>
                  </div>
                </div>
              </div>
            </div>
          )
        ))}
      </div>
    );
  }

  const isScrollable = playersWithStats.length > 8;

  return (
    <div className="w-full border rounded-md overflow-hidden">
  <div className="max-h-[520px] overflow-y-auto w-full">
    <Table className="w-full">
      <TableHeader className="sticky top-0 z-10 bg-white shadow-sm">
        <TableRow>
          <TableHead
            className="w-[160px] cursor-pointer"
            onClick={() => {
              setSortColumn('name');
              setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
            }}
          >
            Player Name
          </TableHead>
          <TableHead
            className="w-[120px] cursor-pointer"
            onClick={() => {
              setSortColumn('whsId');
              setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
            }}
          >
            WHS ID
          </TableHead>
          <TableHead
            className="text-center w-[120px] cursor-pointer"
            onClick={() => {
              setSortColumn('nominated');
              setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
            }}
          >
            Nominated
          </TableHead>
          <TableHead
            className="text-center w-[70px] cursor-pointer"
            onClick={() => {
              setSortColumn('played');
              setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
            }}
          >
            Played
          </TableHead>
          <TableHead className="text-center w-[100px]">W / H / L</TableHead>
          {canEditPlayers && (
            <TableHead className="w-[60px] text-right">Actions</TableHead>
          )}
        </TableRow>
      </TableHeader>
      <TableBody>
        {playersWithStats.map((player) => (
          <TableRow key={player.id} className="bg-muted/30">
            <TableCell className="font-medium">{player.name}</TableCell>
            <TableCell>{player.whsId || 'Not set'}</TableCell>
            <TableCell className="text-center">
              <div className="flex items-center justify-center">
                <Checkbox
                  checked={player.nominated}
                  onCheckedChange={() => {
                    if (canEditNominations && onToggleNomination) {
                      onToggleNomination(player.id);
                    }
                  }}
                  disabled={!canEditNominations}
                  className="border-[#30702F] data-[state=checked]:bg-[#30702F] data-[state=checked]:border-[#30702F]"
                />
              </div>
            </TableCell>
            <TableCell className="text-center w-[70px]">{player.matchesPlayed}</TableCell>
            <TableCell className="text-center w-[100px]">
              {player.matchesWon} / {player.matchesDrawn} / {player.matchesLost}
            </TableCell>
            {canEditPlayers && (
              <TableCell className="text-right">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {onDeletePlayer && (
                      <DropdownMenuItem
                        onClick={() => onDeletePlayer(player.id)}
                        className="text-red-500"
                      >
                        <Trash2 className="h-4 w-4 mr-2" /> Delete
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </div>
</div>
  );
};

export default PlayerTable;